import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaRobot, FaSync } from "react-icons/fa";
import {
  apihubGet,
  apihubPost,
  apihubPut,
} from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const DialogflowCredentials = () => {
  const { projectId } = useProjectPhone();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [hasCredentials, setHasCredentials] = useState(false);
  const [chatbotActive, setChatbotActive] = useState(false);
  const [dialogflowProjectId, setDialogflowProjectId] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const [jsonFile, setJsonFile] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const busy = loading || uploading || syncing || toggling;
  const canToggle = hasCredentials && dialogflowProjectId;

  const fetchDetails = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/getdetailsdialogflow/${projectId}`,
      );
      if (res?.success) {
        setHasCredentials(true);
        setChatbotActive(Boolean(res.isChatbotDialogflowActive));
        setDialogflowProjectId(res.projectId || "");
        setUpdatedAt(res.updatedAt || "");
      } else {
        setHasCredentials(false);
        setChatbotActive(false);
        setDialogflowProjectId("");
        setUpdatedAt("");
      }
    } catch (err) {
      setHasCredentials(false);
      setError(err.message || "Failed to load Dialogflow settings");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    setJsonFile(file || null);
    setError("");
    setMessage("");
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!jsonFile || !projectId) {
      setError("Please select a JSON key file first");
      return;
    }

    setUploading(true);
    setError("");
    setMessage("");
    const isFirstUpload = !dialogflowProjectId;

    try {
      const text = await jsonFile.text();
      const credentials = JSON.parse(text);
      const res = await apihubPost("/googledialogflow/savecredential", {
        userId: projectId,
        credentials,
      });

      if (res?.projectId) setDialogflowProjectId(res.projectId);
      setUpdatedAt(res?.updatedAt || new Date().toISOString());
      setHasCredentials(true);
      setMessage("Credentials saved successfully");
      setJsonFile(null);
      if (fileRef.current) fileRef.current.value = "";

      if (isFirstUpload) {
        try {
          const syncRes = await apihubPost("/googledialogflow/syncdialogflow", {
            userId: projectId,
          });
          const count = syncRes?.count ?? 0;
          setMessage((prev) => `${prev} • Auto-synced ${count} intents`);
          if (syncRes?.updatedAt) setUpdatedAt(syncRes.updatedAt);
        } catch {
          setError(
            "Upload succeeded, but auto-sync failed. Please try Sync.",
          );
        }
      }
    } catch (err) {
      setError(err.message || "Failed to read file or save credentials");
    } finally {
      setUploading(false);
    }
  };

  const onSync = async () => {
    if (!projectId || !hasCredentials) return;
    setSyncing(true);
    setError("");
    setMessage("");
    try {
      const res = await apihubPost("/googledialogflow/syncdialogflow", {
        userId: projectId,
      });
      const count = res?.count ?? 0;
      setMessage(`Synced successfully: ${count} intents imported`);
      if (res?.updatedAt) setUpdatedAt(res.updatedAt);
    } catch (err) {
      setError(err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const onToggleChatbot = async () => {
    if (!projectId || !canToggle) return;
    setToggling(true);
    setError("");
    setMessage("");
    try {
      const next = !chatbotActive;
      await apihubPut(`/googledialogflow/dialogflowchatbot/${projectId}`, {
        isActive: next,
      });
      setChatbotActive(next);
      setMessage(`Chatbot ${next ? "enabled" : "disabled"} successfully`);
    } catch (err) {
      setError(err.message || "Failed to update chatbot status");
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="biz-panel-page biz-dialogflow-page">
      <div className="biz-panel-header biz-dialogflow-header">
        <h1>
          <FaRobot />
          Dialogflow Credentials
        </h1>
        <div className="biz-dialogflow-header-actions">
          {busy && <span className="biz-dialogflow-working">Working…</span>}
          <span className="biz-dialogflow-status-label">
            Chatbot {chatbotActive ? "Enabled" : "Disabled"}
          </span>
          <button
            type="button"
            className={`biz-dialogflow-toggle ${chatbotActive ? "on" : ""}`}
            onClick={onToggleChatbot}
            disabled={!canToggle || busy}
            title={
              canToggle
                ? "Enable/disable chatbot"
                : "Upload credentials to enable the chatbot"
            }
          >
            <span className="biz-dialogflow-toggle-knob" />
          </button>
        </div>
      </div>

      <div className="biz-dialogflow-card">
        {loading ? (
          <p className="biz-panel-hint">Loading settings…</p>
        ) : dialogflowProjectId ? (
          <div className="biz-dialogflow-meta">
            <p>
              <strong>Project ID:</strong> {dialogflowProjectId}
            </p>
            {updatedAt && (
              <p className="biz-panel-hint">
                Last updated: {new Date(updatedAt).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <p className="biz-panel-hint">No credentials uploaded yet.</p>
        )}

        <form className="biz-dialogflow-form" onSubmit={onUpload}>
          <label className="biz-dialogflow-file-label">
            {dialogflowProjectId
              ? "Replace Service Account Key (.json)"
              : "Upload Service Account Key (.json)"}
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              onChange={onFileChange}
              disabled={uploading || loading}
            />
          </label>
          {jsonFile && (
            <p className="biz-panel-hint">
              Selected file: <strong>{jsonFile.name}</strong>
            </p>
          )}
          <button
            type="submit"
            className="biz-btn-primary"
            disabled={uploading || loading || !jsonFile}
          >
            {uploading
              ? "Uploading…"
              : dialogflowProjectId
                ? "Replace Credentials"
                : "Upload Credentials"}
          </button>
        </form>

        {error && <p className="biz-panel-error">{error}</p>}
        {message && <p className="biz-dialogflow-success">{message}</p>}

        <hr className="biz-dialogflow-divider" />

        <button
          type="button"
          className="biz-btn-outline biz-dialogflow-sync-btn"
          onClick={onSync}
          disabled={syncing || loading || !hasCredentials}
        >
          <FaSync />
          {syncing ? "Syncing…" : "Sync Dialogflow Agent"}
        </button>
      </div>
    </div>
  );
};

export default DialogflowCredentials;
